import _ from 'lodash';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';
import Responsive from 'client/components/common/Responsive';
import css from './Graph.scss';

const ticksCount = 7;

function aggregateData(citiesAvg, nationalAvg, years, cities) {
	const sortedYear = years.sort();

	const result = _.map(sortedYear, value => {
		return { name: value };
	});

	const yearIndexes = _.reduce(
		sortedYear,
		(memo, value, index) => {
			memo[value] = index;
			return memo;
		},
		{}
	);

	const cityIndexes = _.reduce(
		cities,
		(memo, value, index) => {
			memo[value] = index;
			return memo;
		},
		{}
	);

	_.mapValues(citiesAvg, value => {
		const index = yearIndexes[value.year];
		const cityIndex = cityIndexes[value.city];
		result[index][cityIndex] = value.avarageScore;
		// result[index][cityIndex] = _.random(21, 88, 2);
	});

	_.mapValues(nationalAvg, value => {
		const index = yearIndexes[value.year];
		result[index].national = value.avarageScore;
		// result[index].national = _.random(21, 88, 2);
	});

	return result;
}

function getTicks(data) {
	const minMax = data.reduce(
		(memo, value) => {
			const values = _.values(_.pick(value, [0, 1, 2, 4, 'national']));
			const min = _.min([memo[0], ...values]);
			const max = _.max([memo[1], ...values]);
			return [+min, +max];
		},
		[100, 0]
	);

	const getTickOffset = () => {
		const min = Math.round(Math.max(0, minMax[0]));
		const max = Math.round(Math.min(100, minMax[1]));
		const count = ticksCount - 1;
		const step = Math.max(1, Math.round((max - min) / count));
		return Math.min(step, 5);
	};
	const tickOffset = getTickOffset();

	return {
		tickOffset,
		ticks: [0].reduce(memo => {
			const min = Math.round(Math.max(0, minMax[0] - tickOffset));
			const max = Math.round(Math.min(100, minMax[1] + tickOffset));
			const count = ticksCount - 1;
			const step = Math.round((max - min) / count);

			memo.push(min);

			for (let i = 1; i <= count; i += 1) {
				const val = min + step * i;
				if (i === count) {
					memo.push(Math.min(100, memo[i - 1] + step));
				} else {
					memo.push(Math.max(val, memo[i - 1] + step));
				}
			}

			return memo;
		}, []),
	};
}

const Graph = props => {
	if (!props.nationalAvg) {
		return null;
	}

	const [data, setData] = useState([]);

	useEffect(() => {
		const nextData = aggregateData(props.citiesAvg, props.nationalAvg, props.years, props.cities);
		setData(nextData);
	}, [props.citiesAvg, props.nationalAvg]);

	if (data.length <= 0) {
		return null;
	}

	// console.info('Graph: ', props, { data });
	const { ticks, tickOffset } = getTicks(data);
	const isMobile = Responsive.isMatching(Responsive.MOBILE);
	const dotRadius = isMobile ? 9 : 10.5;
	const strokeWidth = isMobile ? 4 : 5;

	return (
		<div className={css.comparison}>
			<LineChart width={490} height={370} data={data} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
				<XAxis axisLine={false} tickLine={false} tick={false} padding={{ left: 26, right: 34 }} />
				<YAxis
					ticks={ticks}
					domain={[`dataMin - ${tickOffset}`, `dataMax + ${tickOffset}`]}
					padding={{ top: 10, bottom: 10, left: 0 }}
					tickCount={ticksCount}
					tick={{ strokeWidth: 0, fill: '#000' }}
					tickSize={15}
					tickLine={false}
					axisLine={false}
					allowDecimals={false}
					allowDataOverflow={false}
				/>
				{/* <Tooltip /> */}
				{/* <Legend /> */}
				<CartesianGrid stroke="#CBCBCB" strokeWidth={1} vertical={false} />
				{_.map(props.cities, (city, index) => (
					<Line
						key={city}
						type="linear"
						dataKey={index}
						stroke={Graph.colors.cities[index]}
						strokeWidth={strokeWidth}
						dot={{ fill: Graph.colors.cities[index], r: dotRadius, stroke: 0 }}
						activeDot={{ r: dotRadius }}
					/>
				))}
				<Line
					type="linear"
					dataKey="national"
					stroke={Graph.colors.national}
					strokeWidth={strokeWidth}
					dot={{ fill: Graph.colors.national, r: dotRadius, stroke: 0 }}
					activeDot={{ r: dotRadius }}
				/>
			</LineChart>
		</div>
	);
};

Graph.colors = {
	cities: ['#4bbfcc', '#cc4b4b', '#8bcc4b'],
	national: '#6d6d6d',
};

Graph.propTypes = {
	years: PropTypes.arrayOf(PropTypes.string).isRequired,
	cities: PropTypes.arrayOf(PropTypes.string).isRequired,
	citiesAvg: PropTypes.shape({}),
	nationalAvg: PropTypes.shape({}).isRequired,
};

Graph.defaultProps = {
	citiesAvg: null,
};

export default Graph;
